import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZone,
  adjustToTimeZone,
  DVIR,
  dvirSelector,
  getObjectId, PartialDVIR,
  useGetDVIRQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {DVIRFormValues} from "../../components/form/DVIRForm/DVIRForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useDVIRMutation} from "../../hooks/useDVIRMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {DVIRFormWithSignature, DVIRFormWithSignatureValues} from "../../components/form/DVIRForm/DVIRFormWithSignature";
import {toFormDefaultsFromDVIR} from "../../components/form/DVIRForm/useDVIRForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<DVIRFormValues>
}

export const DVIRFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (<DVIRFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const DVIRFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()
  const {companyId} = useAuth()
  const {hasAccess} = useAuth()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const selectDVIR = useMemo(() => {
    return dvirSelector()
  }, [])

  const {currDVIR, isLoading} = useGetDVIRQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      currDVIR: selectDVIR(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<DVIRFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromDVIR(currDVIR);
  }, [currDVIR, formDefaults]);

  const title: string = useMemo(() => {
    if (currDVIR && !copy) {
      return t("page.entity.dvir.form.title.edit", "Edit DVIR", {ns: "common"});
    } else {
      return t("page.entity.dvir.form.title.new", "New DVIR", {ns: "common"});
    }
  }, [currDVIR, copy, t]);

  const {
    createDVIR,
    updateDVIR,
    isCreating,
    isUpdating,
  } = useDVIRMutation()

  const onDVIRSubmit = useCallback((values: DVIRFormWithSignatureValues) => {

    const formValues = values.form

    const patch: PartialDVIR = {
      dttm: adjustFromTimeZone(formValues.date).getTime(),
      personId: getObjectId(formValues.person),
      personSignatureDttm: adjustFromTimeZone().getTime(),
      personSignatureBase64: values.signature.dataUrl.split(",")[1],
      personSignature2MimeType: values.signature.mimeType,
      trailerId: getObjectId(formValues.trailer),
      vehicleId: getObjectId(formValues.vehicle),
      vehicleAirCompressor: formValues.vehicleAirCompressor,
      vehicleAirLines: formValues.vehicleAirLines,
      vehicleBattery: formValues.vehicleBattery,
      vehicleBrakes: formValues.vehicleBrakes,
      vehicleBrakeAccessories: formValues.vehicleBrakeAccessories,
      vehicleCarburetor: formValues.vehicleCarburetor,
      vehicleClutch: formValues.vehicleClutch,
      vehicleDefroster: formValues.vehicleDefroster,
      vehicleDriveLine: formValues.vehicleDriveLine,
      vehicleEngine: formValues.vehicleEngine,
      vehicleFifthWheel: formValues.vehicleFifthWheel,
      vehicleFrontAxle: formValues.vehicleFrontAxle,
      vehicleFuelTanks: formValues.vehicleFuelTanks,
      vehicleHeater: formValues.vehicleHeater,
      vehicleHorn: formValues.vehicleHorn,
      vehicleLights: formValues.vehicleLights,
      vehicleMirrors: formValues.vehicleMirrors,
      vehicleMuffler: formValues.vehicleMuffler,
      vehicleOilPressure: formValues.vehicleOilPressure,
      vehicleOnBoardRecorder: formValues.vehicleOnBoardRecorder,
      vehicleRadiator: formValues.vehicleRadiator,
      vehicleRearEnd: formValues.vehicleRearEnd,
      vehicleReflectors: formValues.vehicleReflectors,
      vehicleSafetyEquipment: formValues.vehicleSafetyEquipment,
      vehicleSprings: formValues.vehicleSprings,
      vehicleStarter: formValues.vehicleStarter,
      vehicleSteering: formValues.vehicleSteering,
      vehicleTachograph: formValues.vehicleTachograph,
      vehicleTires: formValues.vehicleTires,
      vehicleTransmission: formValues.vehicleTransmission,
      vehicleWheels: formValues.vehicleWheels,
      vehicleWindows: formValues.vehicleWindows,
      vehicleWindshieldWipers: formValues.vehicleWindshieldWipers,
      vehicleOther: formValues.vehicleOther,
      trailerBrakeConnections: formValues.trailerBrakeConnections,
      trailerBrakes: formValues.trailerBrakes,
      trailerCouplingChains: formValues.trailerCouplingChains,
      trailerCouplingPin: formValues.trailerCouplingPin,
      trailerDoors: formValues.trailerDoors,
      trailerHitch: formValues.trailerHitch,
      trailerLandingGear: formValues.trailerLandingGear,
      trailerLights: formValues.trailerLights,
      trailerRoof: formValues.trailerRoof,
      trailerSprings: formValues.trailerSprings,
      trailerTarpaulin: formValues.trailerTarpaulin,
      trailerTires: formValues.trailerTires,
      trailerWheels: formValues.trailerWheels,
      trailerOther: formValues.trailerOther,
      satisfactory: formValues.satisfactory,
      comments: formValues.comments,
      customFieldValues: formValues.custom,
    }

    if (currDVIR && !copy) {
      const dvir: PartialDVIR = {
        ...currDVIR.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateDVIR(dvir, () => navigate(-1))
    } else {
      const dvir: PartialDVIR = {
        ...patch,
        customFieldProfileId: formValues.customFieldProfileId,
        regionId: regionId,
      };
      createDVIR(dvir, () => navigate(-1))
    }
  }, [currDVIR, copy, regionId, createDVIR, updateDVIR, navigate]);

  if (!hasAccess("dispatch_edit")) {
    return <ErrorPage message={t<string>("common.error.unauthorized", "Unauthorized")}/>
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <DVIRFormWithSignature
                  options={{
                    regionId: regionId,
                    companyId: companyId,
                    customFieldProfileId: currDVIR?.customFieldProfileId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onDVIRSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

